/* eslint-disable array-bracket-newline, react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount } from 'wagmi';
import { ICompetitionWatchlistPair, ITicker } from '@kattana-io/common';
import useTrendingItems from '../hooks/useTrendingItems';
import {
  actionAddWatchlist,
  actionRemoveWatchlist,
  actionSetWatchlistTab,
} from '../../../actions/watchlist';
import {
  formatMobileTableData,
  IMobileTableScema,
} from '../../../helpers/formatters';
import useGetWatchlistItems from '../hooks/useGetWatchlistItems';
import DailyPriceChangeMobile from './DailyPriceChange';
import TableCardMobile from '../../../molecules/Table/mobile/TableCard';
import { toFixed2 } from '../../DexTrades/formatters';
import { IMobileFilterProps } from '../../SearchTable/SearchTable';
import WhatchlistTickerMobile from './WatchlistTicker';
import { switchCurrentTicker } from '../../../Authlogic';
import { IStore } from '../../../reducers';
import { WatchlistTab } from '../../../reducers/watchlist';
import { COMPETITION_ENABLED } from '../../../helpers/constants';
import CompetitionTickerMobile from './CompetitionTicker';
import ConnectWarning from '../WatchlistWarning';
import { CEX_WARNING_TEXT } from '..';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';

const MOBILE_TAB_PADDING = 24;
const MOBILE_CEX_WARNING_WIDTH = `calc(100% - ${MOBILE_TAB_PADDING}px)`;

const WATCHLIST_TABS = COMPETITION_ENABLED
  ? [
    WatchlistTab.Star,
    WatchlistTab.Gainers,
    WatchlistTab.TVL,
    WatchlistTab.Competition,
  ]
  : [WatchlistTab.Star, WatchlistTab.Gainers, WatchlistTab.TVL];

const watchlistOptions = WATCHLIST_TABS.map((tab) => ({
  label: tab,
  value: tab,
}));

interface IWhatchlistTableMobileProps {
  inputSearch: string;
  onMount: (data: IMobileFilterProps) => void;
  onBack: () => void;
}

const WhatchlistTableMobile = ({
  inputSearch,
  onMount,
  onBack,
}: IWhatchlistTableMobileProps) => {
  const trending_items = useSelector(
    (state: IStore) => state.registry.trending
  );
  const watchedTickers = useSelector(
    (state: IStore) => state.watchlist.watched
  );
  const watchlistItems = useSelector(
    (state: IStore) => state.watchlist.tickers
  );
  const watchlistCompetition = useSelector(
    (state: IStore) => state.watchlist.competition
  );
  const binance = useSelector(
    (state: IStore) => state.cex_trading.exchanges.binance?.enable
  );
  const tab = useSelector((state: IStore) => state.watchlist.tab);
  const current = useSelector((state: IStore) => state.current);

  const dispatch = useDispatch();
  const { address } = useAccount();
  const [sortedData, setSortedData] = useState<{
    tickers?: Array<ITicker | ICompetitionWatchlistPair>;
    selected?: string;
  }>();
  // const hasAccount = !!account;
  const { trending_items_ids, trending_items_no_ids } =
    useTrendingItems(trending_items);

  // trending tickers
  const trending = useGetWatchlistItems(trending_items_ids.join(','));

  const { collection, searchedSortedData } = useMemo(() => {
    const watchlistData: any[] =
      tab === WatchlistTab.Competition ? watchlistCompetition : watchlistItems;
    const collection = watchlistData.filter((item) => {
      if ('address' in item) {
        return (
          item.name.toLowerCase().includes(inputSearch.toLowerCase()) ||
          item.address.toLowerCase().includes(inputSearch.toLowerCase())
        );
      }
      return (
        item.name.toLowerCase().includes(inputSearch.toLowerCase()) ||
        item.pair.toLowerCase().includes(inputSearch.toLowerCase())
      );
    });

    const searchedSortedData = sortedData?.tickers?.filter((item) => {
      if ('address' in item) {
        return (
          item.name.toLowerCase().includes(inputSearch.toLowerCase()) ||
          item.address.toLowerCase().includes(inputSearch.toLowerCase())
        );
      }
      return (
        item.name.toLowerCase().includes(inputSearch.toLowerCase()) ||
        item.pair.toLowerCase().includes(inputSearch.toLowerCase())
      );
    });
    return {
      collection,
      searchedSortedData,
    };
  }, [
    tab,
    watchlistCompetition,
    watchlistItems,
    sortedData?.tickers,
    inputSearch,
  ]);

  useEffect(() => {
    onMount({
      setSortValue: setSortedData,
      initialData: {
        tickers:
          tab === WatchlistTab.Competition
            ? watchlistCompetition
            : watchlistItems,
        selected: sortedData?.selected,
      },
      tabSelectorData: {
        options: watchlistOptions,
        onSelect: (tab: string) =>
          dispatch(actionSetWatchlistTab(tab as WatchlistTab)),
        initial: { label: tab, value: tab },
        width: 160,
        height: '100%',
      },
    });
  }, [
    sortedData?.selected,
    setSortedData,
    watchlistItems,
    tab,
    watchlistCompetition,
  ]);

  useEffect(() => {
    setSortedData(undefined);
  }, [tab]);

  // const onAccountRequired = useCallback(
  //   () => dispatch(actionShowModal(ModalTypes.connect, {})),
  //   [dispatch]
  // );

  const handleCardClick = useCallback(
    (payload: ITicker) => {
      switchCurrentTicker(payload);
      onBack();
    },
    [dispatch, onBack]
  );

  const onRemove = useCallback(
    (index: number) => dispatch(actionRemoveWatchlist(index)),
    [dispatch]
  );

  const onAdd = useCallback(
    (item: ITicker) => dispatch(actionAddWatchlist(item)),
    [dispatch]
  );

  /**
   * Connect API key
   * @param side
   */
  const onConnectExchange = useCallback(() => {
    if (address) {
      return dispatch(
        actionShowModal(ModalTypes.connectCex, { exchange: 'binance' })
      );
    }
    dispatch(actionShowModal(ModalTypes.connect, {}));
  }, [address, dispatch]);

  const noIdTickersSchema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'label',
        parser: (value) => value,
      },
      item_2: {
        accessor: 'price',
        parser: (value) => `$${toFixed2(value)}`,
      },
    },
  };

  const unfavouriteTickersSchema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'pair',
        parser: (_, row) => (
          <WhatchlistTickerMobile ticker={row} active={current.id === row.id} />
        ),
      },
      item_2: {
        accessor: 'price',
        parser: (_, row) => {
          return <DailyPriceChangeMobile ticker={row} />;
        },
      },
    },
  };

  const favouriteTickersSchema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'pair',
        parser: (_, row) => {
          const isInWatchList =
            tab === WatchlistTab.Star ||
            watchedTickers.some((ticker) => ticker.id === row.id);
          return (
            <WhatchlistTickerMobile
              ticker={row}
              actionRemoveWatchlist={isInWatchList ? onRemove : undefined}
              actionAddWatchlist={isInWatchList ? undefined : onAdd}
              active={current.id === row.id}
            />
          );
        },
      },
      item_2: {
        accessor: 'price',
        parser: (_, row) => {
          return <DailyPriceChangeMobile ticker={row} />;
        },
      },
    },
  };

  const competitionSchema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'pair',
        parser: (_, row) => (
          <CompetitionTickerMobile
            item={row}
            enabled={binance}
            multiplier={row.multiplier_global * row.multiplier_private}
          />
        ),
        width: 130,
      },
      item_2: {
        accessor: 'price',
        parser: (_, row) => {
          return <DailyPriceChangeMobile ticker={row} />;
        },
      },
    },
    row_2: {
      item_1: {
        name: 'Your 24h volume:',
        accessor: 'volume24',
        parser: (value) => `$${toFixed2(value || 0)}`,
        width: 130,
      },
      item_2: {
        name: 'PnL:',
        accessor: 'PNL',
        parser: (value) => `${value > 0 ? '+' : ''}
        ${toFixed2(value || 0)}%`,
      },
    },
    row_3: {
      item_1: {
        name: 'Number of trades:',
        accessor: 'trades',
        parser: (value) => value || 0,
        width: 130,
      },
      item_2: {
        name: 'Earned points:',
        accessor: 'earnedPoints',
        parser: (value) => toFixed2(value || 0),
      },
    },
  };

  const parsedData = ({
    data,
    hasId,
    hasUnfav,
    competition,
  }: {
    data: any;
    hasId?: boolean;
    hasUnfav?: boolean;
    competition?: boolean;
  }) => {
    if (competition) {
      return formatMobileTableData({
        data,
        schema: competitionSchema,
        getColorForItem: () => 'grey',
        handleCardClick,
        keyName: ['id'],
        spaceBetween: true,
      });
    }
    if (hasUnfav) {
      return formatMobileTableData({
        data,
        schema: favouriteTickersSchema,
        getColorForItem: () => 'grey',
        handleCardClick,
        keyName: ['id'],
        spaceBetween: true,
      });
    }
    if (hasId) {
      return formatMobileTableData({
        data,
        schema: unfavouriteTickersSchema,
        getColorForItem: () => 'grey',
        handleCardClick,
        keyName: ['id'],
        spaceBetween: true,
      });
    }
    return formatMobileTableData({
      data,
      schema: noIdTickersSchema,
      getColorForItem: () => 'grey',
      spaceBetween: true,
    });
  };

  const data = searchedSortedData?.length ? searchedSortedData : collection;

  if (tab === WatchlistTab.Competition) {
    return (
      <>
        {parsedData({ data, competition: true }).map((row, index) => (
          <TableCardMobile key={index} data={{ ...row, opened: true }} />
        ))}
        {!binance && (
          <ConnectWarning
            width={MOBILE_CEX_WARNING_WIDTH}
            absolute
            text={CEX_WARNING_TEXT}
            buttonText='Connect API Key'
            onSubmit={onConnectExchange}
          />
        )}
      </>
    );
  }

  return (
    <>
      {parsedData({ data: trending_items_no_ids, hasId: false }).map(
        (row, index) => (
          <TableCardMobile key={index} data={{ ...row, opened: true }} />
        )
      )}
      {parsedData({ data: trending.items, hasId: true }).map((row) => (
        <TableCardMobile key={row.key} data={{ ...row, opened: true }} />
      ))}
      {parsedData({ data, hasUnfav: true }).map((row) => (
        <TableCardMobile key={row.key} data={{ ...row, opened: true }} />
      ))}
    </>
  );
};

export default WhatchlistTableMobile;
